import { Component, Prop, Watch, State } from "@stencil/core";
import {
    Promotion,
    PromotionData,
    PromotionWithTracking,
} from "./async-promotion-response";
import {
    getTrackingData,
    setTrackingEventListeners,
    trackPromotionsShown,
} from "./tracking";
import { shuffle } from "../../utils/shuffle";
import { getConfig, setEnv } from "@trumpf/ux.utils";
import { template, renderLoadingSlideContent } from "./promotion-template";
import waitForHydrated from "../../utils/waitForHydrated";
import api from "../../utils/api";

@Component({
    tag: "ux-iws-async-promotion",
    shadow: false,
})
export class AsyncPromotion {
    /** the API where the data is requested */
    private endpoint =
        "/api/common/businessrule-promotions/1.0/getPromotionsForCustomer/";
    /** when this is set, the component will request data on its own */
    @Prop() locale: string = null;
    /** change only when needed */
    @Prop() env: "prod" | "qa" = "prod";
    /** promotion JSON data - this is an alternative to set the data manually. The component will request the data on its own when the locale is set. */
    @Prop({ mutable: true }) data: PromotionData | null = null;
    /**
     * The CSS-style selector for the element where the promotion should be inserted.
     * If it should be inserted into a slider, reference the <ux-slider> element here
     */
    @Prop() targetSelector: string = "";
    /**
     * This defines in which position the promotion content should be inserted inside the target element (if it is not empty).
     * 0 = first position, 1 = second position, ..., -1 = last position
     */
    @Prop() position: number = 0;
    /** Chose which output mode is used **/
    @Prop() output: "teaser" | "slider" = "teaser";
    /** Maximum number of items that should be inserted. 0 is unlimited **/
    @Prop() maxItems: number = 0;
    /** How the items should be picked from the response (first items vs. random items) */
    @Prop() pickItems: "first" | "random" = "first";
    /** timeout in ms after which the loading indicator for the slider should be removed when no data was received until then */
    @Prop() timeout: number = 2000;
    /** name of the utiility class used to blend out the slider until the loading slide for the promotion is inserted
     * used on the parent element of the slider
     */
    @Prop() opacityUtilityClass: string = "ux-u-opacity-0";

    private inserted = false;
    private timeoutId = null;

    @State() sliderPromotionHasLoaded: null | boolean =
        this.output === "slider" ? false : null;

    componentWillLoad() {
        this.addLoadingSlide();
        this.timeoutId = window.setTimeout(
            () => this.removeLoadingSlide(),
            this.timeout
        );
    }

    componentDidLoad() {
        this.init();
    }

    @Watch("env")
    @Watch("locale")
    private init() {
        if (this.locale !== null) {
            const { env, endpoint, locale } = this;
            setEnv(env);
            const { websiteBaseUrl } = getConfig();
            api<PromotionData>(`${websiteBaseUrl}${endpoint}?locale=${locale}`)
                .then((data) => (this.data = data))
                .catch(() => (this.data = null));
        } else {
            // this is for the legacy implementation where the data is set from outside
            this.insertData();
        }
    }

    @Watch("data")
    private async insertData() {
        if (
            this.inserted ||
            this.data === null ||
            typeof this.data === "undefined" ||
            typeof this.data.promotions === "undefined"
        ) {
            return;
        }
        this.inserted = true;
        // simulating 1 second wait time in storybook
        if (typeof window["__STORYBOOK_ADDONS"] !== undefined) {
            console.log("simulating slow response");
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        const { promotions } = this.data;
        if (promotions.length === 0) {
            this.removeLoadingSlide();
            window.clearTimeout(this.timeoutId);
            return;
        }
        if (this.targetSelector === "") return;
        const target: HTMLElement = document.querySelector(this.targetSelector);
        if (target === null) return;

        // check for all the basic contents
        const filteredPromotions = promotions.filter(
            (promotion) =>
                promotion.linkUrl &&
                promotion.transformedProperties &&
                promotion.transformedProperties.image &&
                promotion.headline
        );
        if (filteredPromotions.length === 0) return;
        const transformedPromotions =
            this.pickItems === "random" ? shuffle([...promotions]) : promotions;
        const pickedPromotions = this.pickPromotions(transformedPromotions);
        const pickedPromotionsWithTracking = getTrackingData(
            pickedPromotions,
            this.position
        );
        this.addContent(target, pickedPromotionsWithTracking);
        trackPromotionsShown(
            pickedPromotionsWithTracking.map(({ tracking }) => tracking)
        );
        setTrackingEventListeners();
        window.clearTimeout(this.timeoutId);
    }

    private async addContent(
        target: HTMLElement,
        promotions: PromotionWithTracking[]
    ) {
        let promotionContents = promotions.map((promotion) =>
            template(promotion)
        );
        // reverse array to add it one by one at the front
        if (this.position !== -1)
            promotionContents = promotionContents.reverse();
        if (this.output === "slider") {
            const slider = target as HTMLUxSliderElement;
            promotionContents.forEach(async (content, index) => {
                if (index === 0 && this.getLoadingSlide() !== null)
                    this.replaceLoadingSlide(content);
                else
                    waitForHydrated(slider, async () => {
                        await slider.addSlide(
                            `<ux-slider-slide>${content}</ux-slider-slide>`,
                            this.position === 0
                        );
                    });
            });
            this.sliderPromotionHasLoaded = true;
            //Update Slider to reset index to 0
            window.setTimeout(() => slider.update(), 100);
            return;
        }
        if (this.output === "teaser") {
            promotionContents.forEach((content) => {
                const container = document.createElement("div");
                container.innerHTML = content;
                if (this.position === 0) {
                    target.prepend(container);
                    return;
                }
                target.append(container);
            });
        }
    }

    private addLoadingSlide = async () => {
        if (this.output !== "slider") return;
        if (this.targetSelector === "") return;
        const target = document.querySelector(
            this.targetSelector
        ) as HTMLUxSliderElement;
        waitForHydrated(target, () => {
            if (this.sliderPromotionHasLoaded === true) return;
            target.addSlide(
                `<ux-slider-slide class="ux-async-promotion__loading-slide">
                    ${renderLoadingSlideContent()}
                </ux-slider-slide>`,
                this.position === 0
            );
            window.setTimeout(async () => {
                await target.update({ autoplay: false });
                target.parentElement.classList.remove(this.opacityUtilityClass);
            }, 100);
        });
    };

    private getLoadingSlide = () => {
        const target = document.querySelector(
            this.targetSelector
        ) as HTMLUxSliderElement;
        const loadingSlide = target.querySelector(
            ".ux-async-promotion__loading-slide"
        );
        return loadingSlide;
    };

    private replaceLoadingSlide = (content: string) => {
        const loadingSlide = this.getLoadingSlide();
        if (loadingSlide === null) return;
        loadingSlide.innerHTML = content;
    };

    private removeLoadingSlide = () => {
        if (this.output !== "slider") return;
        const slider = document.querySelector(
            this.targetSelector
        ) as HTMLUxSliderElement;
        if (this.getLoadingSlide() === null) return;
        slider.removeSlide(this.position);
        slider.update();
    };

    private pickPromotions(promotions: Promotion[]): Promotion[] {
        if (this.maxItems === 0 || promotions.length <= this.maxItems)
            return promotions;
        return promotions.slice(0, this.maxItems);
    }
}
